import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { IMAGES, VALUES } from "@/data/site";
import { CtaBand } from "@/pages/Home";

const AboutHero = () => (
  <section className="bg-navy pt-32 pb-20 md:pt-40 md:pb-28" data-testid="about-hero">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Eyebrow light>About Annwil Law Ja</Eyebrow>
      <h1 className="font-serif font-light text-cream text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[1.02]">
        <MaskedLine delay={0.1}>Trusted guidance.</MaskedLine>
        <MaskedLine delay={0.25}><em className="italic text-gold">Personal attention.</em></MaskedLine>
      </h1>
    </div>
  </section>
);

const FirmIntro = () => (
  <section className="py-24 md:py-32" data-testid="about-intro">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_1.2fr] gap-14">
      <Reveal>
        <Eyebrow>Who We Are</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-navy">
          A practice with a client-first heart.
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed">
        <p>
          Annwil Law Ja advises clients across Jamaica and around the world —
          most of our clients live overseas — in family law, real estate law,
          probate and estate planning. Wherever you are, and wherever in
          Jamaica your matter sits, you receive the same dedicated attention.
        </p>
        <p>
          We believe legal services should be professional and dependable while
          remaining approachable, understandable and focused on the client's
          individual circumstances. From your first consultation, you will know
          where you stand — and what happens next.
        </p>
        <p className="font-serif italic text-navy text-xl md:text-2xl border-l-2 border-gold pl-6">
          “Professional legal service with a personal and approachable touch.”
        </p>
      </Reveal>
    </div>
  </section>
);

const Profile = () => (
  <section className="py-24 md:py-32 bg-sand border-y border-navy/10" data-testid="about-profile">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative overflow-hidden border border-navy/10 group max-w-md">
          <img
            src={IMAGES.principal}
            alt="Anique Wilson Grey, Attorney-at-Law and Founder of Annwil Law Ja"
            className="w-full h-[420px] md:h-[520px] object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-navy/90 px-6 py-4">
            <p className="font-serif text-cream text-xl">Anique Wilson Grey</p>
            <p className="text-gold text-xs uppercase tracking-[0.2em] font-bold mt-1">Attorney-at-Law · Founder, Annwil Law Ja</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <Eyebrow>About Anique Wilson Grey</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-navy">
          Counsel you can sit down and talk with.
        </h2>
        <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">
          Anique Wilson Grey is a legal professional with a diverse background
          spanning law, finance and real estate — experience that brings a
          practical, commercially aware perspective to client matters and a
          strong appreciation for the financial and personal considerations
          that often accompany important legal decisions.
        </p>
        <p className="text-muted-foreground mt-4 text-base md:text-lg leading-relaxed">
          Her time in the financial sector contributes a strong understanding
          of financial matters, documentation, risk and thoughtful planning —
          particularly valuable in estate planning, property-related matters,
          and legal issues with significant financial implications. Her
          practical real estate experience adds first-hand understanding of
          the concerns that arise when individuals and families buy, sell,
          transfer or plan around property assets.
        </p>
        <p className="text-muted-foreground mt-4 text-base md:text-lg leading-relaxed">
          Professional yet approachable, Anique believes clients should feel
          comfortable asking questions and confident that their concerns are
          being heard — building lasting relationships based on trust, respect
          and dependable service.
        </p>
        <Link
          to="/contact"
          data-testid="about-profile-cta"
          className="group inline-flex items-center gap-3 mt-8 text-navy text-sm font-bold uppercase tracking-[0.2em] border-b border-gold pb-1 transition-colors duration-300 hover:text-gold-hover"
        >
          Arrange a meeting
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Values = () => (
  <section className="py-24 md:py-32" data-testid="about-values">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <Eyebrow>Our Values</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-navy max-w-3xl">
          What we stand for.
        </h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10 mt-16">
        {VALUES.map((value, i) => (
          <Reveal key={value.title} delay={i * 0.08} className="bg-cream">
            <div className="p-8 md:p-10 h-full">
              <span className="font-serif italic text-gold-hover text-2xl">{String(i + 1).padStart(2, "0")}.</span>
              <h3 className="font-serif text-2xl text-navy mt-4">{value.title}</h3>
              <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{value.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const APPROACH = [
  {
    number: "01",
    title: "We listen first",
    text: "Your consultation begins with your story — the full picture of your circumstances, concerns and goals.",
  },
  {
    number: "02",
    title: "We explain plainly",
    text: "You receive a clear outline of your legal position, your options, and the likely costs and timelines involved.",
  },
  {
    number: "03",
    title: "We act with care",
    text: "Your matter is handled diligently and discreetly, with regular updates until it is properly resolved.",
  },
];

const Approach = () => (
  <section className="py-24 md:py-32 bg-sand border-t border-navy/10" data-testid="about-approach">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <Eyebrow>Client-Focused Approach</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-navy max-w-3xl">
          How we work with you.
        </h2>
      </Reveal>
      <div className="mt-16">
        {APPROACH.map((step, i) => (
          <Reveal key={step.number} delay={i * 0.08}>
            <div className="grid md:grid-cols-[140px_1fr_1.4fr] gap-4 md:gap-10 items-baseline border-t border-navy/15 py-10">
              <span className="font-serif italic text-gold-hover text-3xl">{step.number}.</span>
              <h3 className="font-serif text-2xl md:text-3xl text-navy font-light">{step.title}</h3>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">{step.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <>
    <AboutHero />
    <EditorialMarquee dark={false} />
    <FirmIntro />
    <Profile />
    <Values />
    <Approach />
    <CtaBand />
  </>
);

export default About;
